/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import CustomerHeader from '../components/CustomerHeader';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import './ServiceBookingPage.css';

const ServiceBookingPage: React.FC = () => {
  const { providerId } = useParams<{ providerId: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'en';
  });
  const [selectedDate, setSelectedDate] = useState('');
  const [calendarDate, setCalendarDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [address, setAddress] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/services/book/${providerId}` } });
    }
  }, [isAuthenticated, navigate, providerId]);

  const [provider, setProvider] = useState<any>(null);
  const [availableSlots, setAvailableSlots] = useState<string[]>(['10:00 AM', '11:00 AM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM']);
  const [error, setError] = useState<string | null>(null);

  // Load provider details and available slots
  useEffect(() => {
    const loadProviderData = async () => {
      if (!providerId) return;
      
      try {
        const providerData = await apiService.getServiceProvider(providerId);
        setProvider(providerData);
        
        // Use provider's available slots if available
        if (providerData.availability?.slots && providerData.availability.slots.length > 0) {
          setAvailableSlots(providerData.availability.slots);
        }
      } catch (err: any) {
        console.warn('Could not load provider data:', err);
        // Continue with default slots
      }
    };

    loadProviderData();
  }, [providerId]);

  // Format date for API
  const getFormattedDate = (): string => {
    if (selectedDate === 'today') {
      return new Date().toISOString().split('T')[0];
    } else if (selectedDate === 'tomorrow') {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow.toISOString().split('T')[0];
    } else if (calendarDate) {
      return calendarDate.toISOString().split('T')[0];
    }
    return '';
  };

  const handleDateOptionChange = (value: string) => {
    setSelectedDate(value);
    setCalendarDate(null); // Clear calendar when selecting quick option
  };

  const handleCalendarDateChange = (date: Date | null) => {
    setCalendarDate(date);
    if (date) {
      setSelectedDate('custom'); // Mark as custom date
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.id) {
      setError('User information not available. Please login again.');
      return;
    }

    const formattedDate = getFormattedDate();
    if (!formattedDate) {
      setError('Please select a date.');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Create booking
      const bookingData = {
        providerId: providerId!,
        customerId: user.id,
        date: formattedDate,
        time: selectedTime,
        address: address.trim(),
        description: description.trim() || undefined
      };

      const bookingResponse = await apiService.createServiceBooking(bookingData);
      
      // Check if this is a mock booking (development fallback)
      if (bookingResponse._isMock) {
        console.log('✅ Using mock booking (API endpoint not available)');
      }
      
      // Navigate to booking confirmation with booking data
      navigate(`/services/booking-confirmation/${providerId}`, {
        state: {
          booking: {
            ...bookingResponse,
            id: bookingResponse.id || bookingResponse.bookingId || `booking-${Date.now()}`,
            providerId: providerId,
            customerId: user.id,
            date: formattedDate,
            time: selectedTime,
            address: address.trim(),
            description: description.trim() || undefined,
            status: bookingResponse.status || 'pending'
          }
        }
      });
    } catch (err: any) {
      console.error('Error creating booking:', err);
      
      // Only show error if booking wasn't successfully created
      // (If it was a 404 and we created a mock booking, the navigation should have happened)
      let errorMessage = 'Failed to create booking. Please try again.';
      
      if (err.message?.includes('404') || err.message?.includes('Not Found')) {
        // For 404, we create a mock booking, so this shouldn't normally be reached
        // But if it is, show a helpful message
        errorMessage = 'Service booking endpoint is not available yet. Please try again or contact support.';
      } else if (err.message?.includes('Network')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <>
      <CustomerHeader
        variant="sticky"
        showTagline
        showVoiceSearch
        showProfileDropdown
        showBackButton
        onBackClick={() => navigate(-1)}
        onSearch={(query) => {
          navigate(`/search?q=${encodeURIComponent(query)}`);
        }}
        onCartClick={() => navigate('/cart')}
      />
      <div className="service-booking-page">

      <main className="booking-main">
        <form onSubmit={handleSubmit} className="booking-form">
          {/* Date Selection */}
          <section className="booking-section">
            <h3 className="section-title">
              {language === 'hi' ? 'तारीख चुनें' : 'Select Date'}
            </h3>
            <div className="date-options">
              <label className="date-option">
                <input
                  type="radio"
                  name="date"
                  value="today"
                  checked={selectedDate === 'today'}
                  onChange={(e) => handleDateOptionChange(e.target.value)}
                />
                <span className="date-label">
                  {language === 'hi' ? 'आज' : 'Today'}
                </span>
              </label>
              <label className="date-option">
                <input
                  type="radio"
                  name="date"
                  value="tomorrow"
                  checked={selectedDate === 'tomorrow'}
                  onChange={(e) => handleDateOptionChange(e.target.value)}
                />
                <span className="date-label">
                  {language === 'hi' ? 'कल' : 'Tomorrow'}
                </span>
              </label>
            </div>
            <div className="calendar-container">
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={language === 'hi' ? 'अन्य तारीख चुनें' : 'Or Select Another Date'}
                  value={calendarDate}
                  onChange={handleCalendarDateChange}
                  minDate={new Date()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          fontSize: '16px',
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </div>
          </section>

          {/* Time Selection */}
          {(selectedDate || calendarDate) && (
            <section className="booking-section">
              <h3 className="section-title">
                {language === 'hi' ? 'समय चुनें' : 'Select Time'}
              </h3>
              <div className="time-slots-grid">
                {availableSlots.map((slot) => (
                  <label key={slot} className="time-slot-option">
                    <input
                      type="radio"
                      name="time"
                      value={slot}
                      checked={selectedTime === slot}
                      onChange={(e) => setSelectedTime(e.target.value)}
                    />
                    <span className="time-slot-label">{slot}</span>
                  </label>
                ))}
              </div>
            </section>
          )}

          {/* Address */}
          <section className="booking-section">
            <h3 className="section-title">
              {language === 'hi' ? 'पता' : 'Service Address'}
            </h3>
            <textarea
              className="address-input"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder={language === 'hi' 
                ? 'सेवा का पूरा पता दर्ज करें...' 
                : 'Enter complete service address...'}
              required
              rows={4}
            />
          </section>

          {/* Description */}
          <section className="booking-section">
            <h3 className="section-title">
              {language === 'hi' ? 'समस्या विवरण' : 'Problem Description'}
            </h3>
            <textarea
              className="description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={language === 'hi'
                ? 'समस्या का विवरण दें...'
                : 'Describe the problem or service needed...'}
              rows={4}
            />
          </section>

          {/* Error Message */}
          {error && (
            <div className="booking-error">
              <span className="error-icon">⚠️</span>
              <span className="error-message">{error}</span>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            className="submit-booking-btn"
            disabled={(!selectedDate && !calendarDate) || !selectedTime || !address || loading}
          >
            {loading 
              ? (language === 'hi' ? 'बुक हो रहा है...' : 'Booking...')
              : (language === 'hi' ? 'बुकिंग कन्फर्म करें' : 'Confirm Booking')
            }
          </button>
        </form>
      </main>
      </div>
    </>
  );
};

export default ServiceBookingPage;








